(() => {
  if (window.cl26PresentationReady) return;
  window.cl26PresentationReady = true;
  const managed = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reconcile() {
    managed.forEach((state, video) => {
      if (!video.isConnected) { managed.delete(video); return; }
      if (document.hidden || reducedMotion.matches) {
        if (!video.paused) { state.resume = true; video.pause(); }
      } else if (state.resume) {
        state.resume = false;
        video.play().catch(() => {});
      }
    });
  }
  const acquisitionManaged = new Map();
  const acquisitionSelector = '[data-cl26-network-acquisition]';
  const acquisitionTimeout = 8000;
  function clearAcquisition(wrapper, state) {
    if (state.timer !== null) window.clearTimeout(state.timer);
    if (state.frame !== null) window.cancelAnimationFrame(state.frame);
    if (state.observer) state.observer.disconnect();
    if (state.resizeObserver) state.resizeObserver.disconnect();
    acquisitionManaged.delete(wrapper);
  }
  function initAcquisition(root) {
    const wrappers = Array.from(root.querySelectorAll(acquisitionSelector));
    if (root.matches && root.matches(acquisitionSelector)) wrappers.unshift(root);
    wrappers.forEach(wrapper => {
      if (wrapper.dataset.acquisitionMode !== 'embedded' || acquisitionManaged.has(wrapper)) return;
      const embed = wrapper.querySelector('[data-cl26-embed]');
      const status = wrapper.querySelector('[data-cl26-acquisition-status]');
      const fallback = wrapper.querySelector('[data-cl26-acquisition-fallback]');
      if (!embed || !status || !fallback) return;
      const display = embed.style.getPropertyValue('display');
      const displayPriority = embed.style.getPropertyPriority('display');
      const state = { timer: null, frame: null, observer: null, resizeObserver: null, ready: false, fallback: false, keepFallback: false };
      acquisitionManaged.set(wrapper, state);
      function showEmbed(show) {
        embed.hidden = !show;
        embed.inert = !show;
        if (show) {
          if (display) embed.style.setProperty('display', display, displayPriority);
          else embed.style.removeProperty('display');
        } else embed.style.setProperty('display', 'none', 'important');
      }
      function observe() {
        if (state.observer) state.observer.observe(embed, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-hidden', 'aria-disabled', 'type'] });
      }
      function update(action) {
        if (state.observer) state.observer.disconnect();
        action();
        if (!state.ready) observe();
      }
      function showFallback() {
        if (state.ready) return;
        const moveFocus = embed.contains(document.activeElement);
        update(() => {
          state.fallback = true;
          showEmbed(false);
          status.hidden = true;
          fallback.hidden = false;
          wrapper.setAttribute('aria-busy', 'false');
          wrapper.dataset.cl26AcquisitionState = 'fallback';
        });
        if (moveFocus) {
          const control = fallback.querySelector('a[href], button:not([disabled]), input:not([disabled])');
          if (control) control.focus({ preventScroll: true });
          else { fallback.setAttribute('tabindex', '-1'); fallback.focus({ preventScroll: true }); }
        }
      }
      function usable(control) {
        if (!control.isConnected || control.disabled || control.getAttribute('aria-disabled') === 'true') return false;
        for (let node = control; node; node = node.parentElement) {
          if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true') return false;
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
        }
        const rect = control.getBoundingClientRect();
        return control.getClientRects().length > 0 && rect.width > 1 && rect.height > 1;
      }
      function check() {
        state.frame = null;
        if (!wrapper.isConnected) { clearAcquisition(wrapper, state); return; }
        if (state.ready) return;
        if (state.fallback && fallback.contains(document.activeElement)) state.keepFallback = true;
        if (state.keepFallback) { update(() => showEmbed(false)); return; }
        let ready = false;
        update(() => {
          fallback.hidden = true;
          showEmbed(true);
          const fields = Array.from(embed.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), select, textarea'));
          const actions = Array.from(embed.querySelectorAll('button:not([type="reset"]), input[type="submit"], input[type="button"], [role="button"]'));
          ready = fields.some(usable) && actions.some(usable);
          if (ready) {
            state.ready = true;
            status.hidden = true;
            wrapper.setAttribute('aria-busy', 'false');
            wrapper.dataset.cl26AcquisitionState = 'ready';
          } else if (state.fallback) {
            showEmbed(false);
            fallback.hidden = false;
          }
        });
        if (ready) {
          if (state.timer !== null) window.clearTimeout(state.timer);
          state.timer = null;
          if (state.observer) state.observer.disconnect();
          if (state.resizeObserver) state.resizeObserver.disconnect();
        }
      }
      function scheduleCheck() {
        if (state.frame === null && !state.ready) state.frame = window.requestAnimationFrame(check);
      }
      const formId = (embed.dataset.formId || '').trim();
      const formVersion = (embed.dataset.formVersion || '').trim();
      if (!formId || !formVersion) { showFallback(); return; }
      if (typeof window.MutationObserver === 'function') state.observer = new window.MutationObserver(scheduleCheck);
      if (typeof window.ResizeObserver === 'function') {
        state.resizeObserver = new window.ResizeObserver(scheduleCheck);
        state.resizeObserver.observe(embed);
      }
      update(() => {
        showEmbed(true);
        status.hidden = false;
        fallback.hidden = true;
        wrapper.setAttribute('aria-busy', 'true');
        wrapper.dataset.cl26AcquisitionState = 'loading';
      });
      state.timer = window.setTimeout(() => {
        state.timer = null;
        check();
        if (!state.ready) showFallback();
      }, acquisitionTimeout);
      check();
    });
  }
  function init(root = document) {
    initAcquisition(root);
    root.querySelectorAll('video[data-cl26-motion]').forEach(video => {
      if (managed.has(video)) return;
      video.muted = true;
      managed.set(video, { resume: true });
    });
    reconcile();
  }
  document.addEventListener('visibilitychange', reconcile);
  reducedMotion.addEventListener('change', reconcile);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    acquisitionManaged.forEach((state, wrapper) => {
      if (event.target === wrapper || event.target.contains(wrapper)) clearAcquisition(wrapper, state);
    });
    managed.forEach((state, video) => {
      if (event.target.contains(video)) { video.pause(); managed.delete(video); }
    });
  });
  init();
})();
